import {
  Component,
  Element,
  Fragment,
  h,
  Host,
  Listen,
  State,
  Watch,
} from "@stencil/core";
import { zapierURL } from "../../api/config";
import {
  fetchDemoPaginatedZaps,
  fetchMorePaginatedZaps,
  fetchPaginatedZaps,
  fetchSession,
  zapsPageSize,
} from "../../api/manage-zaps-iframe";
import { apertureRewriteURL } from "../../lib/aperture-rewrite-url";
import { assertNever } from "../../lib/assertNever";
import { EmbedMode } from "../../lib/workflow-element";
import {
  ArrowSmallLeftIcon,
  ArrowSmallRightIcon,
  TimeIcon,
  WarningIcon,
} from "../../lib/icons";
import { getAnchorTarget } from "../../lib/link-target";
import { Logger } from "../../lib/logger";
import * as WFE from "../../lib/manage-zaps";
import { urlWithQuACParams } from "../../lib/quac";
import { urlWithReferrer } from "../../lib/referrer";
import { rootElementMetadata } from "../../lib/root-element-metadata";
import {
  ManageZapsIframeState,
  reducer,
  StateCheckingSession,
} from "./reducers";
import {
  enableInternalPopupBlocker,
  getFakeSession,
  waitForLogIn,
  waitForPopupClose,
} from "./utils";

/**
 * Rather than the embedMode requested by the Full Zapier Experience, this is
 * the observed embedMode as detected by the Manage Zaps page inside a popup or
 * iframe.
 */
const determineInternalEmbedMode = ({
  clientId,
}: {
  clientId: string | undefined;
}): EmbedMode => {
  return window.top === window.self && clientId === undefined
    ? "popup"
    : "iframe";
};

const determineInternalEmbedModeFromState = ({
  state,
}: {
  state: ManageZapsIframeState;
}) => {
  let clientId: string | undefined;
  if ("props" in state) {
    clientId = state.props.clientID;
  }
  return determineInternalEmbedMode({ clientId });
};
// *-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*
//
// NOTE: This element is loaded inside an iframe served on zapier.com. As such,
// this code has full access to the Zapier user's information. DO NOT send any
// private information via `parent.postMessage`! This would defeat the security
// of running the code inside an iframe.
//
// *-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*

const Spinner = ({ ariaLabel }: { ariaLabel: string }) => (
  <span aria-label={ariaLabel} class="spinner" role="status" />
);

@Component({
  tag: "zapier-internal-manage-zaps-iframe",
  styleUrl: "zapier-internal-manage-zaps-iframe.css",
  shadow: true,
})
export class ZapierInternalManageZapsIframe {
  @State() private state: ManageZapsIframeState = { type: "pending" };
  @State() private wasPopupBlocked = false;
  @State() private hasStorageAccess = false;
  @State() private storageAccessBlocked = false;

  @Element() private element!: HTMLZapierInternalManageZapsIframeElement;

  private _height = 0;
  private abortController = new AbortController();

  /**
   * Receive `postMessage` from the parent
   */
  @Listen("message", { target: "window" })
  _onMessage(event: MessageEvent) {
    this.state = reducer(this.state, event.data);
  }

  /**
   * Watch for state updates and perform side effects (such as network requests)
   */
  @Watch("state")
  async update() {
    if ("props" in this.state) {
      if (this.state.props.internalPopupBlockerEnabled) {
        enableInternalPopupBlocker();
      }
      if (this.state.props.internalDemoMode === "cookies-blocked") {
        this.storageAccessBlocked = true;
        // Avoid infinite recursion by double checking we're not already in the
        // `signed-out` state... which would then trigger another call to
        // `update` since it watches changes to `state`
        if (this.state.type !== "signed-out") {
          this.state = {
            type: "signed-out",
            props: this.state.props,
          };
        }
        return;
      }
    }
    switch (this.state.type) {
      case "checking-session": {
        this._updateLoading(this.state);
        break;
      }
      case "refreshing": {
        this.state = { ...this.state, type: "loaded" };
        this._loadMoreZaps("refresh", this.state.currentURL);
        break;
      }
      case "errored": {
        Logger.error(this.state.error);
        break;
      }
    }
  }

  private async _updateLoading(state: StateCheckingSession) {
    try {
      this.state = { type: "loading", props: state.props };
      const session = this.state.props.internalDemoMode
        ? getFakeSession(this.state.props.internalDemoMode)
        : await fetchSession();
      const { slug, selectedAPI } = this.state.props;
      if (session.is_logged_in) {
        this.state = {
          type: "signed-in",
          session: session,
          props: state.props,
        };
        const zaps = this.state.props.internalDemoMode
          ? await fetchDemoPaginatedZaps({
              selectedAPI,
              page: 0,
              mode: this.state.props.internalDemoMode,
            })
          : await fetchPaginatedZaps({ slug, selectedAPI });
        this.state = {
          type: "loaded",
          session: session,
          props: this.state.props,
          appSlug: slug,
          appSelectedAPI: selectedAPI,
          page: 0,
          count: zaps.count,
          currentURL: zaps.currentURL && apertureRewriteURL(zaps.currentURL),
          nextURL: zaps.nextURL && apertureRewriteURL(zaps.nextURL),
          previousURL: zaps.previousURL && apertureRewriteURL(zaps.previousURL),
          zaps: zaps.results,
        };
      } else {
        this.state = {
          type: "signed-out",
          props: state.props,
        };
      }
    } catch {
      this.state = {
        type: "signed-out",
        props: state.props,
      };
    }
    if (this.state.type === "loaded") {
      this._sendUpdate({
        type: "zapier-elements.workflow.update.logged-in",
      });
    } else {
      this._sendUpdate({
        type: "zapier-elements.workflow.update.logged-out",
      });
      if (
        determineInternalEmbedModeFromState({ state: this.state }) === "popup"
      ) {
        this._handleLogin("sign-up");
      }
    }
  }

  private _visitFirstParty = async () => {
    if (this.state.type !== "signed-out") {
      return;
    }
    this.state = { type: "signing-in", props: this.state.props };
    const url = new URL("/partner/popup-info", location.href);
    const popup = window.open(
      url.href,
      "zapier.partner.popup-info",
      "width=900,height=750",
    );
    if (!popup) {
      this.wasPopupBlocked = true;
      this.state = {
        type: "signed-out",
        props: this.state.props,
      };
      return;
    }
    await waitForPopupClose(popup);
    this.storageAccessBlocked = false;
    this.state = { type: "checking-session", props: this.state.props };
  };

  private _signUp = async () => {
    await this._handleLogin("sign-up");
  };

  private _login = async () => {
    await this._handleLogin("login");
  };

  private _handleLogin = async (mode: "login" | "sign-up") => {
    // *************************************************************************
    // This function is used as a click handler. "User gesture" processing for
    // permissions on the popup and storage access API calls are highly
    // dependent on how browsers handle user gestures. Safari explicitly allows
    // click handlers to request storage access AND a popup in the same click,
    // but we are not allowed to _check_ storage access in this same click.
    // Along those same lines, we CANNOT use fetch or any other type of network
    // call within this handler function. Get your work done before this
    // function!
    // *************************************************************************
    if (!("props" in this.state)) {
      return;
    }
    this.state = { type: "signing-in", props: this.state.props };

    // Get partner service slug for OIDC flow
    const {
      clientID: clientId,
      slug,
      referrer,
      oidcEnabled,
      quacEnabled,
      signUpFirstName,
      signUpLastName,
      signUpEmail,
      utm,
    } = this.state.props;
    const internalEmbedMode = determineInternalEmbedMode({ clientId });
    let url = new URL(location.href);
    // quac takes precenence over oidc if enabled
    if (quacEnabled) {
      url.pathname = "/quac";
      url = new URL(
        urlWithQuACParams(url.href, {
          clientId,
          signUpFirstName,
          signUpLastName,
          signUpEmail,
        }),
      );
    } else if (oidcEnabled) {
      url.pathname = "/partner/iframe/login";
    } else if (mode === "sign-up") {
      url.pathname = "/sign-up";
    } else {
      url.pathname = "/app/login";
    }
    url.searchParams.set("skipOnboarding", "true");
    if (internalEmbedMode === "popup") {
      url.searchParams.set(
        "next",
        location.pathname + location.search + location.hash,
      );
    } else {
      url.searchParams.set("next", "/partner/popup-close");
    }
    if (referrer) {
      url.searchParams.set("referer", referrer);
    }
    if (!quacEnabled) {
      url.searchParams.set("provider", slug);
    }
    if (quacEnabled) {
      url.searchParams.set("type", "quac");
    } else if (oidcEnabled) {
      url.searchParams.set("type", "oidc");
    }
    for (const [key, value] of Object.entries(utm)) {
      url.searchParams.set(key, value);
    }

    // Request access to use 3rd party cookies within an iframe. Safari
    // explicitly allows this API to be used in combination with `window.open`
    // in a single click, despite the fact that normally you can't make async
    // calls before using `window.open`.
    //
    // https://webkit.org/blog/11545/updates-to-the-storage-access-api/
    //
    // https://developer.mozilla.org/en-US/docs/Web/Privacy/Storage_Access_Policy#storage_access_grants
    //
    // DO NOT PUT THE FOLLOWING BLOCK INTO AN ASYNC FUNCTION. Adding additional
    // async functions will revoke the use gesture grant that is returned by
    // `requestStorageAccess`.

    // You would think we should use the Storage Access API as it's intended to
    // be used, but right now Safari is throwing exceptions when we try to use
    // it, for reasons I can't quite figure out. Luckily both Safari and Firefox
    // include automatic storage access grants for users who visit the iframe's
    // domain inside a popup window from the 3rd party page. Ideally this would
    // be a separate page that merely reacquires "storage access" instead of
    // actually making the user go through the entire login flow again
    // unncessarily. The user MUST interact with the page in order for the grant
    // to work, though. So we can build another page that checks if the user is
    // logged in, and lets them skip the the log in flow by clicking a button to
    // skip it and go to the popup close page. Right now the `/app/login` route
    // always forces a log-in, even if the user is already logged in. This is
    // done so that users can more easily switch accounts, I heard, though it
    // feels weird to me still.
    //
    // Sage Fennel (2022-10-12)

    if (!this.hasStorageAccess) {
      if (document.requestStorageAccess) {
        try {
          await document.requestStorageAccess();
          this.hasStorageAccess = true;
        } catch (err) {
          Logger.error("Storage Access API was blocked", err);
          this.storageAccessBlocked = true;
          this.state = {
            type: "signed-out",
            props: this.state.props,
          };
          return;
        }
      }
    }

    if (internalEmbedMode === "popup") {
      this._navigateToURL(url.href);
      return;
    }

    // Open the popup and check the session endpoint when it's closed. Once we
    // have 3rd party cookie access, we can get the cookie that was set inside
    // the popup authentication. Due to COOP we cannot hold a reference to the
    // popup window, so we need to use polling to figure out when the process
    // has completed.
    //
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cross-Origin-Opener-Policy
    //
    window.open(url.href, "zapier.partner.login", "width=900,height=750");
    this.abortController.abort();
    this.abortController = new AbortController();
    await waitForLogIn({ signal: this.abortController.signal });
    this.state = { type: "checking-session", props: this.state.props };
  };

  private _sendUpdate(update: WFE.Update): void {
    let posted = false;
    // Normally an iframe would hard code which origins it would `postMessage`
    // to/from, but we rely instead on `frame-ancestors` from
    // `Content-Security-Policy` to allowlist the proper origins only
    if (window.parent) {
      window.parent.postMessage(update, "*");
      posted = true;
    }
    // Now that we also support opening this page in a popup, we need to be
    // mindful about the fact that we can't easily stop pages from embedding us
    // and reading our `postMessage` values. Don't send any sensitive data
    // through this channel.
    if (window.opener) {
      window.opener.postMessage(update, "*");
      posted = true;
    }
    if (!posted) {
      Logger.error("No window.parent or window.opener to postMessage to");
    }
  }

  async componentWillLoad() {
    // The Storage Access API is a way for iframes to access cookies in browsers
    // with iframe-based cookie blocking (such as Safari and Firefox).
    //
    // In browsers that support this feature, we must use async functions to check
    // and request acccess to the user's cookies, otherwise they will appear logged
    // out inside this iframe, even though we are on zapier.com.
    //
    // https://developer.mozilla.org/en-US/docs/Web/API/Storage_Access_API
    //
    // Please read this blog post from the Safari team for an explanation of how the
    // new "Storage Access API" works, as it's 100% required in order for our
    // logged-in iframe experiences to work on 3rd party domains, and it's pretty
    // tricky!
    //
    // https://webkit.org/blog/11545/updates-to-the-storage-access-api/
    //
    if (document.hasStorageAccess) {
      this.hasStorageAccess = await document.hasStorageAccess();
    } else {
      this.hasStorageAccess = true;
    }
  }

  componentDidLoad() {
    this._sendUpdate({ type: "zapier-elements.workflow.update.loaded" });
  }

  connectedCallback() {
    // Hide scrollbar inside the iframe. We use `postMessage` to ask the parent
    // frame to resize our iframe to the correct height, and this will avoid any
    // situation where we might get a "flicker of scrollbar" for a frame, if
    // somehow `requestAnimationFrame` fails us (I swear I've seen this a couple
    // times).
    document.documentElement.style.overflow = "hidden";
    // We should detect when the CSS font has loaded, or if something otherwise
    // causes the iframe content to grow in size, but, this works as a quick
    // fix... Sorry!
    //
    // Poll the height before every frame is rendered in the browser. This
    // SOUNDS computationally expensive, and you want to be careful when adding
    // tasks during animation frames, but in my testing this was virtually
    // unnoticeable.
    const pollHeight = () => {
      this._sendHeight();
      if (this.element.isConnected) {
        requestAnimationFrame(pollHeight);
      }
    };
    pollHeight();
  }

  private _navigateToURL(url: string): void {
    this.state = {
      type: "navigating",
      destinationURL: url,
    };
    // Give the main thread time to update the UI before we navigate
    setTimeout(() => {
      location.href = url;
    }, 0);
  }

  /** Tell the parent our new scroll height so we can be resized */
  private _sendHeight() {
    const height = this.element.clientHeight;
    if (height !== this._height) {
      this._height = height;
      this._sendUpdate({
        type: "zapier-elements.workflow.update.height-changed",
        height,
      });
    }
  }

  private _loadMoreZaps = async (
    action: "refresh" | "next" | "previous",
    url: string,
  ): Promise<void> => {
    if (this.state.type !== "loaded") {
      throw new Error("must be signed in");
    }
    let page = this.state.page;
    if (action === "next") {
      page++;
    } else if (action === "previous") {
      page = Math.max(0, page - 1);
    }
    try {
      this.state = { ...this.state, type: "loading-paginated-zaps" };
      const {
        nextURL: next,
        results,
        count,
        previousURL: previous,
      } = this.state.props.internalDemoMode
        ? await fetchDemoPaginatedZaps({
            selectedAPI: this.state.props.selectedAPI,
            page,
            mode: this.state.props.internalDemoMode,
          })
        : await fetchMorePaginatedZaps(url);
      this.state = {
        ...this.state,
        type: "loaded",
        zaps: results,
        page,
        count,
        currentURL: url,
        nextURL: next && apertureRewriteURL(next),
        previousURL: previous && apertureRewriteURL(previous),
      };
    } catch (err) {
      this.state = { ...this.state, type: "loaded" };
      throw err;
    }
  };

  render() {
    const hostProps = {
      ...rootElementMetadata,
      "data-embed-mode": determineInternalEmbedModeFromState({
        state: this.state,
      }),
    };
    switch (this.state.type) {
      case "navigating":
        return (
          <Host {...hostProps}>
            <div class="sign-in-container">
              <Spinner ariaLabel="Loading Zapier Editor..." />
            </div>
          </Host>
        );
      case "pending": {
        return (
          <Host {...hostProps}>
            <div class="sign-in-container">
              <Spinner ariaLabel="Pending..." />
            </div>
          </Host>
        );
      }
      case "checking-session": {
        return (
          <Host {...hostProps} data-theme={this.state.props.theme}>
            <div class="sign-in-container">
              <Spinner ariaLabel="Loading..." />
            </div>
          </Host>
        );
      }
      case "signing-in":
      case "signed-out": {
        return (
          <Host {...hostProps} data-theme={this.state.props.theme}>
            <div class="sign-in-container">
              {this.state.props.internalFromWorkflowElement ? null : (
                <h2 class="sign-in-title">
                  Sign up or log in to Zapier to see your automated workflows
                </h2>
              )}
              {this.storageAccessBlocked ? (
                <button
                  key="sign-in"
                  type="button"
                  class="button allow-cookies"
                  onClick={this._visitFirstParty}
                >
                  Allow cookies
                </button>
              ) : (
                <div class="sign-up-or-log-in-container">
                  <button
                    key="sign-up"
                    type="button"
                    class="button"
                    onClick={this._signUp}
                  >
                    Sign up
                  </button>
                  <button
                    key="sign-in"
                    type="button"
                    data-button-type="secondary"
                    class="button"
                    onClick={this._login}
                  >
                    Log in
                  </button>
                </div>
              )}
              {this.storageAccessBlocked && (
                <p class="popup-blocked">
                  <WarningIcon /> Please allow cookies from Zapier to continue.
                </p>
              )}
              {this.wasPopupBlocked && (
                <Fragment>
                  <p class="popup-blocked">
                    <WarningIcon /> Failed to open popup. Please allow popups in
                    your browser and try again.
                  </p>
                  <button
                    type="button"
                    class="button sign-in"
                    onClick={() => {
                      if (!(this.state.type === "signed-out")) {
                        return;
                      }
                      this.state = {
                        type: "checking-session",
                        props: this.state.props,
                      };
                    }}
                  >
                    Finish signing in
                  </button>
                </Fragment>
              )}
            </div>
          </Host>
        );
      }
      case "loading":
      case "signed-in": {
        return (
          <Host {...hostProps} data-theme={this.state.props.theme}>
            <div class="sign-in-container">
              <Spinner ariaLabel="Loading workflows..." />
            </div>
          </Host>
        );
      }
      case "errored": {
        return (
          <Host {...hostProps} data-theme={this.state.props.theme}>
            Error
          </Host>
        );
      }
      case "refreshing":
      case "loading-paginated-zaps":
      case "loaded": {
        const {
          appSlug,
          zaps,
          session,
          type,
          nextURL,
          previousURL,
          count,
          page,
          props: { theme, linkTarget, referrer, utm },
        } = this.state;

        const internalEmbedMode = determineInternalEmbedMode({
          clientId: this.state.props.clientID,
        });

        const displayName = `${session.first_name} ${session.last_name}`;
        const pageCount = Math.max(1, Math.ceil(count / zapsPageSize));
        const isLoading = type === "loading-paginated-zaps";
        const userInitials =
          session.first_name.charAt(0) + session.last_name.charAt(0);
        const newWindowText =
          linkTarget === "new-tab" && "(opens in new window)";
        const showPaginationControls = count > 0 && (previousURL || nextURL);

        const createZapURL = new URL("/webintent/create-zap", zapierURL);
        createZapURL.search = new URLSearchParams({
          create: "true",
          "entry-point-location": "explore",
          "entry-point-method": "connect_call_to_action",
          provider: appSlug,
          ...utm,
        }).toString();

        const createZapURLWithReferrer = urlWithReferrer(
          createZapURL.href,
          referrer,
        );

        const onCreateZapClick = (event: MouseEvent) => {
          if (internalEmbedMode === "popup") {
            event.preventDefault();
            event.stopPropagation();
            this._navigateToURL(createZapURL.href);
            return;
          }
          if (
            "props" in this.state &&
            this.state.props.linkTarget === "modal"
          ) {
            event.preventDefault();
            event.stopPropagation();
            this._sendUpdate({
              type: "zapier-elements.workflow.update.modal-open",
              url: createZapURLWithReferrer,
            });
          }
        };

        return (
          <Host {...hostProps} data-theme={theme}>
            <div class="account" data-loading={isLoading}>
              <a
                aria-label={`Create workflow ${newWindowText}`}
                class="button"
                href={createZapURLWithReferrer}
                referrerPolicy="no-referrer-when-downgrade"
                target={getAnchorTarget(linkTarget)}
                onClick={onCreateZapClick}
              >
                Create workflow
              </a>
              <div class="account-info" aria-label={displayName} role="img">
                {session.photo_url ? (
                  <img src={session.photo_url} alt="" />
                ) : (
                  <span>{userInitials}</span>
                )}
              </div>
            </div>
            <ul class="zaps-container" data-loading={isLoading}>
              {zaps.length === 0 && (
                <li class="zap-empty-container">
                  <h2 class="zap-empty-title">
                    You don&rsquo;t have any workflows set up yet
                  </h2>
                  <p class="zap-empty-subtitle">
                    Connect your apps and automate workflows by creating a
                    workflow.
                  </p>
                </li>
              )}
              {zaps.map((z) => {
                const zapHistoryURL = new URL("/app/history", zapierURL);
                zapHistoryURL.search = new URLSearchParams({
                  root_id: `${z.id}`,
                  "entry-point-location": "partner_embed",
                  provider: appSlug,
                  ...utm,
                }).toString();
                const zapURL = new URL(`/editor/${z.id}`, zapierURL);
                zapURL.search = new URLSearchParams({
                  "entry-point-location": "partner_embed",
                  provider: appSlug,
                  ...utm,
                }).toString();
                const zapURLWithReferrer = urlWithReferrer(
                  zapURL.href,
                  referrer,
                );
                const zapHistoryURLWithReferrer = urlWithReferrer(
                  zapHistoryURL.href,
                  referrer,
                );
                return (
                  <li class="zap-element">
                    <zapier-prefab-row
                      onModalOpen={(event) => {
                        if (internalEmbedMode === "iframe") {
                          this._sendUpdate({
                            type: "zapier-elements.workflow.update.modal-open",
                            url: event.detail.url,
                          });
                        } else {
                          this._navigateToURL(zapURLWithReferrer);
                        }
                      }}
                      linkTarget={linkTarget}
                      services={z.nodes.flatMap((node) =>
                        node.service ? [node.service] : [],
                      )}
                      referrer={referrer}
                      url={zapURLWithReferrer}
                      zapState={z.paused ? "off" : "on"}
                      zapTitle={z.title || "Untitled workflow"}
                      callToAction=""
                    >
                      <div class="zap-footer" slot="footer-start">
                        <div
                          class="account-info"
                          aria-label={displayName}
                          role="img"
                        >
                          {session.photo_url ? (
                            <img src={session.photo_url} alt="" />
                          ) : (
                            <span>{userInitials}</span>
                          )}
                        </div>
                        <span class="zap-owner-name">
                          Owned by <b>{displayName}</b>
                        </span>
                      </div>
                      <div slot="footer-end">
                        <a
                          class="zap-history-link"
                          href={zapHistoryURLWithReferrer}
                          // This URL is _not safe_ to open in an iframe modal, so
                          // we expliclitly don't add an `onClick` here and use
                          // `getAnchorTarget`'s "new tab" default behavior
                          target={getAnchorTarget(linkTarget)}
                          onClick={(event) => {
                            if (this.state.type !== "loaded") {
                              return;
                            }
                            if (internalEmbedMode === "popup") {
                              event.preventDefault();
                              this._navigateToURL(zapHistoryURLWithReferrer);
                              return;
                            }
                            if (this.state.props.internalDemoMode) {
                              event.preventDefault();
                              window.alert(
                                "Users can view their workflow history such as the number of workflows that ran and tasks automated.",
                              );
                              return;
                            }
                          }}
                        >
                          <TimeIcon /> Workflow history
                        </a>
                      </div>
                    </zapier-prefab-row>
                  </li>
                );
              })}
            </ul>
            <div
              class="loading-overlay"
              hidden={type !== "loading-paginated-zaps"}
            >
              <Spinner ariaLabel="Loading..." />
            </div>
            {showPaginationControls && (
              <Fragment>
                <div class="zaps-pagination-container" data-loading={isLoading}>
                  <button
                    type="button"
                    aria-label="Previous workflows"
                    class="button button-pagination"
                    disabled={type === "loading-paginated-zaps" || !previousURL}
                    onClick={() => {
                      if (!previousURL) {
                        return;
                      }
                      this._loadMoreZaps("previous", previousURL);
                    }}
                  >
                    <ArrowSmallLeftIcon />
                  </button>
                  <span class="zaps-count">
                    {page + 1} of {pageCount}
                  </span>
                  <button
                    type="button"
                    aria-label="Next workflows"
                    class="button button-pagination"
                    disabled={type === "loading-paginated-zaps" || !nextURL}
                    onClick={() => {
                      if (!nextURL) {
                        return;
                      }
                      this._loadMoreZaps("next", nextURL);
                    }}
                  >
                    <ArrowSmallRightIcon />
                  </button>
                </div>
              </Fragment>
            )}
          </Host>
        );
      }
      default: {
        assertNever(this.state);
      }
    }
  }
}
